import { CallDto } from '@act/shared/data-transfer-objects';
import { useEffect, useRef } from 'react';
import { useToast } from 'react-components/app/ReusableToast';
import { IconLibrary } from '@act/storybook';
import useConfirmationAlertDialog from '@act/react-components/dialog-forms/hooks/useConfirmationAlertDialog.hook';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { callsRequests } from 'redux/requests/calls.request';
import { isActiveCallConnection } from './activeCallConnection.util';

/**
 * Merge, leave, and add-guide API actions for the active call menu.
 *
 * @param call Active call DTO.
 * @param allConnections All connections on the call (unfiltered).
 * @param guideConnection Acting user's live connection, if any.
 * @param onAddGuideSuccess Resets add-participant UI after a successful invite.
 * @returns Action handlers and loading flags.
 */
export function useActiveCallMenuActions(
  call: CallDto,
  allConnections: CallDto['connections'],
  guideConnection: CallDto['connections'][0] | null | undefined,
  onAddGuideSuccess: () => void
) {
  const toast = useToast();
  const openConfirmationDialog = useConfirmationAlertDialog();

  const [addedGuide, isAddingGuideToCall, addGuideToCall] = useApiCall(
    callsRequests.addGuideToCall
  );
  const [mergedCall, isMergingCalls, mergeCallConnections] = useApiCall(
    callsRequests.mergeCallConnections
  );
  const [_hangupResult, isHangingUpGuide, hangupGuideConnection] = useApiCall(
    callsRequests.hangupConnection
  );

  const lastProcessedGuideRef = useRef<typeof addedGuide>(null);

  useEffect(() => {
    if (
      addedGuide &&
      !isAddingGuideToCall &&
      addedGuide !== lastProcessedGuideRef.current
    ) {
      toast('Guide added successfully', 'success');
      onAddGuideSuccess();
      lastProcessedGuideRef.current = addedGuide;
    }
  }, [addedGuide, isAddingGuideToCall, toast, onAddGuideSuccess]);

  useEffect(() => {
    if (mergedCall && !isMergingCalls) {
      toast('Calls merged successfully', 'success');
    }
  }, [mergedCall, isMergingCalls, toast]);

  const handleMergeCall = () => {
    mergeCallConnections({
      body: { callId: call.id },
    });
  };

  const handleLeaveCall = async () => {
    if (!guideConnection) {
      return;
    }

    const activeCount = allConnections.filter(isActiveCallConnection).length;
    const willCallContinue = activeCount >= 3;
    const message = willCallContinue
      ? 'You will leave the call and the existing participants will continue their call.'
      : 'Are you sure you want to leave the call? This will end the call for all participants.';

    const shouldLeave = await openConfirmationDialog({
      title: 'Leave call confirmation',
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      icon: <IconLibrary iconName="Warning" size={42} color="white" />,
      message,
      confirmationText: 'Leave call',
      confirmButtonColor: 'error',
    });

    if (shouldLeave) {
      hangupGuideConnection({
        body: {
          connectionId: guideConnection.id,
        },
      });
    }
  };

  const submitAddGuide = (guideId: string, memberId?: string | null) => {
    addGuideToCall({
      params: { callId: call.id },
      body: {
        guideId,
        isColdTransfer: false,
        ...(memberId && { memberId }),
      },
    });
  };

  return {
    isAddingGuideToCall,
    isMergingCalls,
    isHangingUpGuide,
    handleMergeCall,
    handleLeaveCall,
    submitAddGuide,
  };
}
